import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as api from './api';
import * as crypto from './crypto';
import AppFeedbackModals from './components/app/AppFeedbackModals';
import AppMainContent from './components/app/AppMainContent';
import AppModalLayer from './components/app/AppModalLayer';
import AuthScreen from './components/auth/AuthScreen';
import UnlockScreen from './components/auth/UnlockScreen';
import SetupWizard from './components/auth/SetupWizard';
import FirstRunOnboarding from './components/onboarding/FirstRunOnboarding';
import { parseToGaiaID, displayGaiaID } from './utils/gaiaAddress';
import { sanitizeAvatarFile } from './utils/avatar';
import { useTranslation } from './utils/i18n';
import { buildRecoveryBackup, downloadRecoveryFile, parseRecoveryBackup } from './utils/recovery';
import NavigationSidebar from './components/layout/NavigationSidebar';
import ListPane from './components/layout/ListPane';

// Utilities & Hooks
import { buildInitialKeyHistory, appendKeyHistory } from './utils/keyHistory';
import useGaiaAuth from './hooks/useGaiaAuth';
import useVault from './hooks/useVault';
import useGaiaDrop from './hooks/useGaiaDrop';
import useChat from './hooks/useChat';
import useEmails from './hooks/useEmails';
import usePublicChannels from './hooks/usePublicChannels';

// Security invariant checked by adversarial tests:
// untrusted: !!(env.Untrusted || env.untrusted || isLegacySmtp)

function mergeContactRecords(cachedContacts = [], backendContacts = []) {
  const merged = new Map();
  cachedContacts.forEach(contact => {
    const key = String(contact?.gaiaID || contact?.ID || '').toLowerCase();
    if (key) {
      merged.set(key, contact);
    }
  });
  backendContacts.forEach(contact => {
    const key = String(contact?.gaiaId || contact?.gaiaID || contact?.ID || '').toLowerCase();
    if (!key) return;
    const cached = merged.get(key) || {};
    merged.set(key, {
      ...cached,
      ...contact,
      gaiaID: contact.gaiaId || contact.gaiaID || cached.gaiaID || '',
      publicKey: contact.publicKey || cached.publicKey || '',
      displayName: contact.displayName || cached.displayName || '',
      blocked: contact.blocked ?? cached.blocked ?? false,
      trustPassport: cached.trustPassport || null,
      abuseScore: cached.abuseScore || { score: 0, escalationLevel: 0 },
      keyHistory: cached.keyHistory || (contact.publicKey ? buildInitialKeyHistory(contact.publicKey, true) : [])
    });
  });
  return Array.from(merged.values());
}

export default function App() {
  const { language, changeLanguage, t } = useTranslation();
  // Theme Switching
  const [isLightMode, setIsLightMode] = useState(() => localStorage.getItem('gaia_theme') === 'light');

  useEffect(() => {
    if (isLightMode) {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
      localStorage.setItem('gaia_theme', 'light');
    } else {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
      localStorage.setItem('gaia_theme', 'dark');
    }
  }, [isLightMode]);

  // Mail List Collapsible State
  const [mailListCollapsed, setMailListCollapsed] = useState(false);

  // Quantum shield explanation popup state
  const [showQuantumShieldModal, setShowQuantumShieldModal] = useState(false);

  // Mobile menu control state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Custom Alerts/Popups States
  const [alertConfig, setAlertConfig] = useState(null); // { title, text, type }
  const [confirmConfig, setConfirmConfig] = useState(null);

  const triggerAlert = useCallback((title, text, type = 'success') => {
    setAlertConfig({ title, text, type });
  }, []);

  const showConfirm = useCallback((title, text, onConfirm, onCancel = null, confirmText = null, cancelText = null, danger = false) => {
    setConfirmConfig({
      showThreeButtons: false,
      title,
      text,
      onConfirm,
      onCancel,
      confirmText: confirmText || t('bestaetigen') || 'Bestätigen',
      cancelText: cancelText || t('abbrechen') || 'Abbrechen',
      danger
    });
  }, [t]);

  const showConfirmThreeButtons = useCallback((title, text, onConfirm, onConfirmAlternative, confirmText, confirmAlternativeText, cancelText, onCancel = null) => {
    setConfirmConfig({
      showThreeButtons: true,
      title,
      text,
      onConfirm,
      onConfirmAlternative,
      confirmText,
      confirmAlternativeText,
      cancelText,
      onCancel
    });
  }, []);

  // Core navigation & data
  const [currentMenu, setCurrentMenu] = useState('dashboard'); // 'dashboard', 'inbox', 'smtp_inbox', 'sent', 'groups', 'chat', 'contacts', 'profile'
  const [activeProfileSection, setActiveProfileSection] = useState(() => (typeof window !== 'undefined' && window.innerWidth > 992) ? 'edit' : null);
  const [publicChannelCreatorOpen, setPublicChannelCreatorOpen] = useState(false);
  const [identities, setIdentities] = useState([]);
  const [activeIdentity, setActiveIdentity] = useState(null);
  const [activeIdentityRoles, setActiveIdentityRoles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [presenceMap, setPresenceMap] = useState({});

  // PWA Install Prompt State
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log('PWA installation choice:', outcome);
    setDeferredPrompt(null);
  };

  // User Profile States
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profileAvatar, setProfileAvatar] = useState('\u{1F916}');
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [areKeysUnlocked, setAreKeysUnlocked] = useState(false);
  const [profilePasswordInput, setProfilePasswordInput] = useState('');
  const [profileUnlockError, setProfileUnlockError] = useState('');
  const [cryptoSessionMinutes, setCryptoSessionMinutesState] = useState(() => {
    const stored = Number(localStorage.getItem('gaia_crypto_session_minutes') || '0');
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
  });
  const [inactivityLockMinutes, setInactivityLockMinutesState] = useState(() => {
    const stored = Number(localStorage.getItem('gaia_inactivity_lock_minutes') || '15');
    return Number.isFinite(stored) && stored >= 0 ? stored : 15;
  });
  const [pinUnlockEnabled, setPinUnlockEnabled] = useState(() => !!localStorage.getItem('gaia_pin_mnemonic_enc'));
  const [messageMeta, setMessageMeta] = useState({});
  const [activeDirectUnreadMarker, setActiveDirectUnreadMarker] = useState(null);
  const [activeGroupUnreadMarker, setActiveGroupUnreadMarker] = useState(null);
  const [showBootSequence, setShowBootSequence] = useState(false);
  const [showFirstRunOnboarding, setShowFirstRunOnboarding] = useState(false);
  const directUnreadSnapshotKeyRef = useRef('');
  const groupUnreadSnapshotKeyRef = useRef('');
  const chatKeyCheckRef = useRef('');
  const knownChatNotificationIdsRef = useRef(new Set());
  const chatNotificationsPrimedRef = useRef(false);
  const bootSequenceSeenRef = useRef('');
  const onboardingSeenKeyRef = useRef('');

  // Add Contact Modal States
  const [showAddContact, setShowAddContact] = useState(false);
  const [discoverGaiaId, setDiscoverGaiaId] = useState('');
  const [discoveredContact, setDiscoveredContact] = useState(null);
  const [discoverError, setDiscoverError] = useState('');
  const [contactProfile, setContactProfile] = useState(null);

  // Key change confirmation modal state (Platin UX)
  const [keyChangeWarning, setKeyChangeWarning] = useState(null);
  const [keyChangeConfirmInput, setKeyChangeConfirmInput] = useState('');

  const fileInputRef = useRef(null);

  async function fetchIdentities() {
    if (!user) return;
    try {
      const list = await api.getMyIdentities();
      setIdentities(list || []);
      if (list && list.length > 0) {
        const cached = localStorage.getItem(`active_identity_${user.id}`);
        const found = list.find(id => id.GaiaID === cached || id.ID === cached);
        setActiveIdentity(found || list[0]);
      } else {
        setActiveIdentity(null);
        setShowWizard(true);
      }
    } catch (_) {}
  }

  useEffect(() => {
    if (!activeIdentity) {
      setActiveIdentityRoles([]);
      return;
    }
    const fetchActiveRoles = async () => {
      try {
        const res = await api.getGovernanceRoles();
        if (res && res.roles) {
          setActiveIdentityRoles(res.roles);
        } else {
          setActiveIdentityRoles([]);
        }
      } catch (_) {
        setActiveIdentityRoles([]);
      }
    };
    fetchActiveRoles();
  }, [activeIdentity]);

  const getSenderRoles = useCallback((senderGaiaOrID) => {
    if (!senderGaiaOrID) return [];
    const parsed = parseToGaiaID(senderGaiaOrID);
    
    if (activeIdentity && parseToGaiaID(activeIdentity.GaiaID) === parsed) {
      return activeIdentityRoles;
    }
    
    const contact = contacts.find(c => parseToGaiaID(c.gaiaID) === parsed);
    if (contact?.trustPassport?.roles) {
      return contact.trustPassport.roles;
    }
    
    return [];
  }, [activeIdentity, activeIdentityRoles, contacts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearAllData = useCallback(() => {
    setInboxEmails([]);
    setSentEmails([]);
    setChatMessages([]);
    setMessageMeta({});
    setRooms([]);
    setActiveRoom(null);
    setChannels([]);
    setActiveChannel(null);
    setActiveIdentity(null);
    setIdentities([]);
    setSelectedMail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1. Auth Hook
  const {
    user, setUser,
    mnemonic, setMnemonic,
    derivedKeys, setDerivedKeys,
    isRegister, setIsRegister,
    usernameInput, setUsernameInput,
    passwordInput, setPasswordInput,
    authError, setAuthError,
    showRegSuccessPopup, setShowRegSuccessPopup,
    isLocked,
    unlockPassword, setUnlockPassword,
    unlockError,
    tempUserId,
    showWizard, setShowWizard,
    wizardStep, setWizardStep,
    copiedMnemonic, setCopiedMnemonic,
    wizardGaiaUsername, setWizardGaiaUsername,
    wizardDomain, setWizardDomain,
    wizardCustomDomain, setWizardCustomDomain,
    wizardFallbackNodes, setWizardFallbackNodes,
    wizardError,
    availableNodes,
    serverVersion,
    serverConsensus,
    handleWizardRegisterIdentity,
    handleGenerateMnemonic,
    handleAuthSubmit,
    handleUnlock,
    handleLogout,
    handleLock,
    writeCryptoSession
  } = useGaiaAuth({
    triggerAlert,
    fetchIdentities: () => fetchIdentities(),
    clearAllData,
    cryptoSessionMinutes
  });

  const [showTransition, setShowTransition] = useState(false);
  const [transitionText, setTransitionText] = useState('GaiaCom');
  const prevUserRef = React.useRef(user);
  const prevIsLockedRef = React.useRef(isLocked);

  React.useEffect(() => {
    if (!prevUserRef.current && user) {
      prevUserRef.current = user;
      setTransitionText('GaiaCom');
      setShowTransition(true);
      const timer = setTimeout(() => setShowTransition(false), 2000);
      return () => clearTimeout(timer);
    }
    prevUserRef.current = user;
  }, [user]);

  React.useEffect(() => {
    if (prevIsLockedRef.current && !isLocked && user) {
      prevIsLockedRef.current = isLocked;
      setTransitionText('GaiaCom Vault');
      setShowTransition(true);
      const timer = setTimeout(() => setShowTransition(false), 2000);
      return () => clearTimeout(timer);
    }
    prevIsLockedRef.current = isLocked;
  }, [isLocked, user]);

  function verifyRecipientsAndRun(recipientGaiaIds, pubRecords, runFn) {
    for (let i = 0; i < recipientGaiaIds.length; i++) {
      const gaiaID = recipientGaiaIds[i];
      const pubRecord = pubRecords[i];
      
      const formatted = parseToGaiaID(gaiaID);
      const stored = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
      const fetchedKey = pubRecord.public_keys.identity;
      
      if (stored && stored.publicKey !== fetchedKey) {
        setKeyChangeWarning({
          gaiaID: formatted,
          displayName: stored.displayName,
          oldKey: stored.publicKey,
          newKey: fetchedKey,
          resumeFn: () => {
            const updatedStored = {
              ...stored,
              publicKey: fetchedKey,
              keyHistory: appendKeyHistory(stored, fetchedKey, true),
              keyConfirmedAt: new Date().toISOString()
            };
            const updated = contacts.map(c => c.ID === stored.ID ? updatedStored : c);
            setContacts(updated);
            localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            verifyRecipientsAndRun(recipientGaiaIds, pubRecords, runFn);
          },
          cancelFn: () => {
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            triggerAlert('Abgebrochen', 'Sicherheitskritischer SchlÃƒÂ¼sselwechsel nicht bestÃƒÂ¤tigt.', 'danger');
          }
        });
        return;
      }
    }
    runFn();
  }

  // 2. Vault Hook
  const {
    vaultUnlocked,
    vaultPasswordInput, setVaultPasswordInput,
    vaultError,
    vaultRecords,
    selectedVaultRecord, setSelectedVaultRecord,
    vaultDraftTitle, setVaultDraftTitle,
    vaultDraftCategory, setVaultDraftCategory,
    vaultDraftBody, setVaultDraftBody,
    handleUnlockVault,
    handleDeleteVaultRecord,
    handleLockVault
  } = useVault({ user, triggerAlert });

  // 3. GaiaDrop Hook
  const {
    dropTargetInput, setDropTargetInput,
    dropSenderInput, setDropSenderInput,
    dropMessageInput, setDropMessageInput,
    dropStatus,
    dropError,
    gaiaDropInbox,
    gaiaDropLoading,
    selectedDrop, setSelectedDrop,
    handleSubmitPublicGaiaDrop,
    loadGaiaDropInbox,
    handleSelectDrop,
    handleDeleteDrop
  } = useGaiaDrop({
    activeIdentity,
    derivedKeys,
    triggerAlert,
    showConfirm,
    t
  });

  // 4. Emails Hook
  const {
    inboxEmails, setInboxEmails,
    setSentEmails,
    selectedMail, setSelectedMail,
    selectedMailProof,
    isComposing, setIsComposing,
    composeTo, setComposeTo,
    composeSubject, setComposeSubject,
    composeBody, setComposeBody,
    setComposeReplyTo,
    composeScheduledFor, setComposeScheduledFor,
    isSmtpMode, setIsSmtpMode,
    composeError,
    uploadProgress,
    uploadFile,
    pollEmails,
    handleSendMail,
    handleReplyMail,
    handleFileUpload,
    handleReportMail,
    handleExportDisclosurePackage,
    markMessagesAsRead,

    // New mailbox state exports
    allMails,
    mailThreads,
    mailboxFolder, setMailboxFolder,
    mailboxSearch, setMailboxSearch,
    mailboxLabel, setMailboxLabel,
    labelsList,
    draftsList,
    filterRules,
    mailSettings,
    isSavingDraft,
    updateMailboxState,
    snoozeMail,
    saveLabel,
    saveFilterRule,
    saveSettings,
    activeDraftIdRef
  } = useEmails({
    activeIdentity,
    derivedKeys,
    contacts,
    setContacts,
    user,
    triggerAlert,
    showConfirm,
    t,
    setChatMessages: (chats) => setChatMessages(chats),
    verifyRecipientsAndRun
  });

  // 5. Chat Hook
  const {
    rooms, setRooms,
    activeRoom, setActiveRoom,
    channels, setChannels,
    activeChannel, setActiveChannel,
    showCreateGroupModal, setShowCreateGroupModal,
    showJoinGroupModal, setShowJoinGroupModal,
    showCreateChannelModal, setShowCreateChannelModal,
    showGroupSettingsModal, setShowGroupSettingsModal,
    groupNameInput, setGroupNameInput,
    groupDescriptionInput, setGroupDescriptionInput,
    groupAvatarInput, setGroupAvatarInput,
    isCrisisRoomInput, setIsCrisisRoomInput,
    editGroupName, setEditGroupName,
    editGroupDescription, setEditGroupDescription,
    editGroupAvatar, setEditGroupAvatar,
    editGroupIsCrisis, setEditGroupIsCrisis,
    editGroupIsPrivate, setEditGroupIsPrivate,
    editGroupReadOnly, setEditGroupReadOnly,
    editGroupSlowModeSeconds, setEditGroupSlowModeSeconds,
    joinGroupHashInput, setJoinGroupHashInput,
    newChannelNameInput, setNewChannelNameInput,

    chatMessages, setChatMessages,
    activeChatContact, setActiveChatContact,
    chatInputText, setChatInputText,
    showEmojiPicker, setShowEmojiPicker,
    messageReplyTarget, setMessageReplyTarget,
    fetchRooms,
    handleCreateRoom,
    handleJoinRoom,
    handleLeaveRoom,
    handleCreateChannel,
    handleUpdateMemberRole,
    handleOpenGroupSettings,
    handleUpdateGroupSettings,
    handleDeleteGroup,
    handleSendChatMessage,
    handleSendGroupMessage,
    handleDeleteChatMessage,
    handleEditChatMessage,
    handleClearDirectChat,
    handleClearGroupChannel,
    uploadProgress: chatUploadProgress,
    uploadChatFile,
    toggleBlockContact,
    slowModeCooldowns,
    pinnedMessageIds,
    joinRequests,
    moderationLogs,
    publicRoomsSearchResult,
    handleToggleMessagePin,
    handleKickMember,
    handleTransferOwnership,
    handleGetJoinRequests,
    handleModerateJoinRequest,
    handleGetModerationLogs,
    handleSearchPublicRooms,
    handleCreateRoomInviteLink,
    handleJoinViaInviteLink,
    handleCreateJoinRequest
  } = useChat({
    activeIdentity,
    derivedKeys,
    contacts,
    setContacts,
    user,
    triggerAlert,
    showConfirm,
    showConfirmThreeButtons,
    t,
    pollEmails,
    verifyRecipientsAndRun
  });

  const {
    publicChannels,
    activePublicChannel,
    setActivePublicChannel,
    publicChannelPosts,
    publicChannelsError,
    publicChannelsLoading,
    publicChannelPostsLoading,
    refreshPublicChannels,
    createChannel: createPublicChannel,
    updateChannel: updatePublicChannel,
    toggleSubscription: togglePublicChannelSubscription,
    createPost: createPublicChannelPost,
    togglePostReaction: togglePublicChannelPostReaction,
    createPostComment: createPublicChannelPostComment,
    togglePostPin: togglePublicChannelPostPin,
    updateChannelComments: updatePublicChannelComments,
    reportChannel,
    deleteChannel,
    verifyChannel,
    discoverResults,
    discoverLoading,
    handleBlockChannel,
    handleUnblockChannel,
    handleDiscoverChannels,
    handleDeleteComment,
    handleModerateComment
  } = usePublicChannels({
    activeIdentity,
    user,
    triggerAlert,
    enabled: currentMenu === 'public_channels'
  });

  useEffect(() => {
    if (!activeIdentity?.ID || !user) {
      setPresenceMap({});
      return undefined;
    }
    let stopped = false;
    const sendHeartbeat = async () => {
      try {
        await api.sendPresenceHeartbeat(activeIdentity.ID, 'online');
      } catch (_) {}
    };
    sendHeartbeat();
    const interval = window.setInterval(() => {
      if (!stopped) {
        sendHeartbeat();
      }
    }, 30000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [activeIdentity?.ID, user]);

  useEffect(() => {
    if (!activeIdentity?.ID || !user) {
      setPresenceMap({});
      return undefined;
    }
    const gaiaIds = contacts
      .map(contact => contact.gaiaID)
      .filter(Boolean)
      .filter(gaiaID => parseToGaiaID(gaiaID) !== parseToGaiaID(activeIdentity.GaiaID));
    if (gaiaIds.length === 0) {
      setPresenceMap({});
      return undefined;
    }
    let stopped = false;
    const loadPresence = async () => {
      try {
        const result = await api.getPresenceStatus(gaiaIds);
        if (!stopped) {
          setPresenceMap(result?.presence || {});
        }
      } catch (_) {}
    };
    loadPresence();
    const interval = window.setInterval(() => {
      if (!stopped) {
        loadPresence();
      }
    }, 20000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [activeIdentity?.GaiaID, activeIdentity?.ID, contacts, user]);

  useEffect(() => {
    if (!user) {
      setMessageMeta({});
      return;
    }
    try {
      const stored = localStorage.getItem(`gaia_message_meta_${user.id}`);
      setMessageMeta(stored ? JSON.parse(stored) : {});
    } catch (_) {
      setMessageMeta({});
    }
  }, [user]);

  const updateMessageMeta = useCallback((updater) => {
    if (!user) return;
    setMessageMeta(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem(`gaia_message_meta_${user.id}`, JSON.stringify(next));
      return next;
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setMessageMeta(prev => {
      let changed = false;
      const next = { ...prev };
      const hasPinnedLabel = labels => Array.isArray(labels) && labels.includes('chat-pinned');
      for (const message of chatMessages) {
        if (!message?.id) continue;
        const serverReactions = message.reactions || {};
        const serverReactedByMe = message.reactedByMe || {};
        const serverSaved = !!message.mailbox?.isStarred;
        const serverPinned = hasPinnedLabel(message.mailbox?.labels);
        const current = next[message.id] || {};
        if (
          JSON.stringify(current.reactions || {}) !== JSON.stringify(serverReactions) ||
          JSON.stringify(current.reactedByMe || {}) !== JSON.stringify(serverReactedByMe) ||
          !!current.saved !== serverSaved ||
          !!current.pinned !== serverPinned
        ) {
          next[message.id] = {
            ...current,
            reactions: serverReactions,
            reactedByMe: serverReactedByMe,
            saved: serverSaved,
            pinned: serverPinned
          };
          changed = true;
        }
      }
      if (changed) {
        localStorage.setItem(`gaia_message_meta_${user.id}`, JSON.stringify(next));
      }
      return changed ? next : prev;
    });
  }, [chatMessages, user]);

  const toggleMessagePin = useCallback(async (messageId) => {
    if (!messageId || !activeIdentity?.ID) return;
    const message = chatMessages.find(entry => entry.id === messageId);
    if (!message) return;
    let previousMeta = null;
    const nextPinned = !(messageMeta[messageId]?.pinned);
    updateMessageMeta(prev => {
      previousMeta = prev[messageId] || {};
      return {
        ...prev,
        [messageId]: {
          ...previousMeta,
          pinned: nextPinned
        }
      };
    });
    try {
      const labels = Array.isArray(message.mailbox?.labels) ? [...message.mailbox.labels] : [];
      const nextLabels = nextPinned
        ? Array.from(new Set([...labels.filter(label => label !== 'chat-pinned'), 'chat-pinned']))
        : labels.filter(label => label !== 'chat-pinned');
      await api.updateMailboxStates(activeIdentity.ID, [{
        messageId,
        folder: message.mailbox?.folder || 'inbox',
        isRead: !!message.isRead,
        isStarred: !!message.mailbox?.isStarred,
        isImportant: !!message.mailbox?.isImportant,
        isSpam: !!message.mailbox?.isSpam,
        isArchived: !!message.mailbox?.isArchived,
        labels: nextLabels
      }]);
      pollEmails();
    } catch (err) {
      updateMessageMeta(prev => ({
        ...prev,
        [messageId]: previousMeta
      }));
      triggerAlert(t('fehler') || 'Fehler', err.message || 'Pin konnte nicht gespeichert werden.', 'danger');
    }
  }, [activeIdentity?.ID, chatMessages, messageMeta, pollEmails, t, triggerAlert, updateMessageMeta]);

  const toggleMessageSaved = useCallback(async (messageId) => {
    if (!messageId || !activeIdentity?.ID) return;
    const message = chatMessages.find(entry => entry.id === messageId);
    if (!message) return;
    let previousMeta = null;
    const nextSaved = !(messageMeta[messageId]?.saved);
    updateMessageMeta(prev => {
      previousMeta = prev[messageId] || {};
      return {
        ...prev,
        [messageId]: {
          ...previousMeta,
          saved: nextSaved
        }
      };
    });
    try {
      await api.updateMailboxStates(activeIdentity.ID, [{
        messageId,
        folder: message.mailbox?.folder || 'inbox',
        isRead: !!message.isRead,
        isStarred: nextSaved,
        isImportant: !!message.mailbox?.isImportant,
        isSpam: !!message.mailbox?.isSpam,
        isArchived: !!message.mailbox?.isArchived,
        labels: Array.isArray(message.mailbox?.labels) ? message.mailbox.labels : []
      }]);
      pollEmails();
    } catch (err) {
      updateMessageMeta(prev => ({
        ...prev,
        [messageId]: previousMeta
      }));
      triggerAlert(t('fehler') || 'Fehler', err.message || 'Gespeichert-Status konnte nicht gespeichert werden.', 'danger');
    }
  }, [activeIdentity?.ID, chatMessages, messageMeta, pollEmails, t, triggerAlert, updateMessageMeta]);

  const reactToMessage = useCallback(async (messageId, emoji) => {
    if (!messageId || !emoji || !activeIdentity?.ID) return;
    let previousMeta = null;
    updateMessageMeta(prev => {
      const current = prev[messageId] || {};
      previousMeta = current;
      const reactions = { ...(current.reactions || {}) };
      const reactedByMe = { ...(current.reactedByMe || {}) };
      if (reactedByMe[emoji]) {
        reactions[emoji] = Math.max(0, Number(reactions[emoji] || 0) - 1);
        if (reactions[emoji] === 0) delete reactions[emoji];
        delete reactedByMe[emoji];
      } else {
        reactions[emoji] = Number(reactions[emoji] || 0) + 1;
        reactedByMe[emoji] = true;
      }
      return {
        ...prev,
        [messageId]: {
          ...current,
          reactions,
          reactedByMe
        }
      };
    });
    try {
      const reactionState = await api.toggleMessageReaction(activeIdentity.ID, messageId, emoji);
      updateMessageMeta(prev => ({
        ...prev,
        [messageId]: {
          ...(prev[messageId] || {}),
          reactions: reactionState?.reactions || {},
          reactedByMe: reactionState?.reactedByMe || {}
        }
      }));
    } catch (err) {
      updateMessageMeta(prev => {
        const next = { ...prev };
        if (previousMeta) {
          next[messageId] = previousMeta;
        } else {
          delete next[messageId];
        }
        return next;
      });
      triggerAlert(t('fehler') || 'Fehler', err.message || 'Reaction konnte nicht gespeichert werden.', 'danger');
    }
  }, [activeIdentity, updateMessageMeta, triggerAlert, t]);

  const setCryptoSessionMinutes = useCallback((minutes) => {
    const safeMinutes = Math.max(0, Number(minutes) || 0);
    setCryptoSessionMinutesState(safeMinutes);
    localStorage.setItem('gaia_crypto_session_minutes', String(safeMinutes));
    if (safeMinutes === 0) {
      sessionStorage.removeItem('gaia_crypto_session');
    }
  }, []);

  const setInactivityLockMinutes = useCallback((minutes) => {
    const safeMinutes = Math.max(0, Number(minutes) || 0);
    setInactivityLockMinutesState(safeMinutes);
    localStorage.setItem('gaia_inactivity_lock_minutes', String(safeMinutes));
  }, []);

  const handleUpdatePrivacySettings = useCallback(async (allowAnonymousStats) => {
    const result = await api.updatePrivacySettings(allowAnonymousStats);
    const nextAllow = result.allowAnonymousStats !== false;
    setUser(prev => prev ? { ...prev, allowAnonymousStats: nextAllow } : prev);
    triggerAlert(
      'Privacy updated',
      nextAllow
        ? 'Anonymous aggregate statistics are enabled for this account.'
        : 'Anonymous aggregate statistics are disabled for this account.',
      'success'
    );
  }, [setUser, triggerAlert]);

  useEffect(() => {
    if (currentMenu !== 'chat' || !activeChatContact || !activeIdentity) {
      setActiveDirectUnreadMarker(null);
      directUnreadSnapshotKeyRef.current = '';
      return;
    }
    const contactGaia = parseToGaiaID(activeChatContact.gaiaID);
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    const snapshotKey = `${ownGaia}|${contactGaia}`;
    if (directUnreadSnapshotKeyRef.current === snapshotKey) {
      return;
    }
    const unreadMessages = chatMessages.filter(msg =>
      parseToGaiaID(msg.sender) === contactGaia &&
      parseToGaiaID(msg.recipient) === ownGaia &&
      !msg.isRead
    );
    directUnreadSnapshotKeyRef.current = snapshotKey;
    setActiveDirectUnreadMarker({
      contactGaiaId: contactGaia,
      firstUnreadMessageId: unreadMessages[0]?.id || '',
      count: unreadMessages.length
    });
  }, [activeChatContact, activeIdentity, chatMessages, currentMenu]);

  useEffect(() => {
    if (currentMenu !== 'chat' || !activeChatContact || !activeIdentity) return;
    const contactGaia = parseToGaiaID(activeChatContact.gaiaID);
    const ids = chatMessages
      .filter(msg => parseToGaiaID(msg.sender) === contactGaia && parseToGaiaID(msg.recipient) === parseToGaiaID(activeIdentity.GaiaID))
      .map(msg => msg.id);
    markMessagesAsRead(ids);
  }, [activeChatContact, activeIdentity, chatMessages, currentMenu, markMessagesAsRead]);

  useEffect(() => {
    if (currentMenu !== 'chat' || !activeChatContact?.gaiaID) {
      chatKeyCheckRef.current = '';
      return;
    }

    const formatted = parseToGaiaID(activeChatContact.gaiaID);
    const stored = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
    const knownKey = stored?.publicKey || activeChatContact.publicKey || '';
    if (!knownKey) {
      return;
    }

    const snapshotKey = `${formatted}|${knownKey}`;
    if (chatKeyCheckRef.current === snapshotKey || keyChangeWarning?.gaiaID === formatted) {
      return;
    }

    let cancelled = false;
    const verifyActiveChatKey = async () => {
      try {
        const res = await api.getPublicIdentity(formatted);
        if (cancelled || !res?.publicRecord) {
          return;
        }

        const pubRecord = JSON.parse(res.publicRecord);
        const fetchedKey = pubRecord?.public_keys?.identity || '';
        if (!fetchedKey) {
          chatKeyCheckRef.current = snapshotKey;
          return;
        }

        if (fetchedKey === knownKey) {
          chatKeyCheckRef.current = snapshotKey;
          return;
        }

        setKeyChangeWarning({
          gaiaID: formatted,
          displayName: stored?.displayName || activeChatContact.displayName || formatted,
          oldKey: knownKey,
          newKey: fetchedKey,
          resumeFn: () => {
            const baseContact = stored || activeChatContact;
            const updatedStored = {
              ...baseContact,
              publicKey: fetchedKey,
              trustPassport: res.trustPassport || baseContact.trustPassport,
              keyHistory: appendKeyHistory(baseContact, fetchedKey, true),
              keyConfirmedAt: new Date().toISOString()
            };
            const updatedContacts = contacts.map(contact =>
              parseToGaiaID(contact.gaiaID) === formatted ? updatedStored : contact
            );
            setContacts(updatedContacts);
            if (user?.id) {
              localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updatedContacts));
            }
            setActiveChatContact(updatedStored);
            chatKeyCheckRef.current = `${formatted}|${fetchedKey}`;
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
          },
          cancelFn: () => {
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            setActiveChatContact(null);
            triggerAlert(
              t('key_change_warn_title') || 'Sicherheitswarnung',
              t('key_change_warn_desc') || 'Dieser Kontakt-Schlüssel hat sich geändert. Der Chat wurde geschlossen, bis der Fingerprint verifiziert ist.',
              'danger'
            );
          }
        });
      } catch (_) {
        chatKeyCheckRef.current = snapshotKey;
      }
    };

    verifyActiveChatKey();
    return () => {
      cancelled = true;
    };
  }, [activeChatContact, contacts, currentMenu, keyChangeWarning, setActiveChatContact, t, triggerAlert, user?.id]);

  useEffect(() => {
    if (currentMenu !== 'groups' || !activeRoom || !activeIdentity) {
      setActiveGroupUnreadMarker(null);
      groupUnreadSnapshotKeyRef.current = '';
      return;
    }
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    const snapshotKey = `${activeRoom.ID}|${activeChannel?.id || ''}|${ownGaia}`;
    if (groupUnreadSnapshotKeyRef.current === snapshotKey) {
      return;
    }
    const unreadMessages = chatMessages
      .filter(msg =>
        msg.roomId === activeRoom.ID &&
        (!activeChannel?.id || msg.channelId === activeChannel.id) &&
        parseToGaiaID(msg.sender) !== ownGaia &&
        msg.sender !== activeIdentity.ID &&
        !msg.isRead
      )
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    groupUnreadSnapshotKeyRef.current = snapshotKey;
    setActiveGroupUnreadMarker({
      roomId: activeRoom.ID,
      channelId: activeChannel?.id || '',
      firstUnreadMessageId: unreadMessages[0]?.id || '',
      count: unreadMessages.length
    });
  }, [activeChannel?.id, activeIdentity, activeRoom, chatMessages, currentMenu]);

  useEffect(() => {
    if (currentMenu !== 'groups' || !activeRoom || !activeIdentity) return;
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    const ids = chatMessages
      .filter(msg =>
        msg.roomId === activeRoom.ID &&
        parseToGaiaID(msg.sender) !== ownGaia &&
        msg.sender !== activeIdentity.ID
      )
      .map(msg => msg.id);
    markMessagesAsRead(ids);
  }, [activeIdentity, activeRoom, chatMessages, currentMenu, markMessagesAsRead]);

  useEffect(() => {
    if (!activeIdentity?.GaiaID) {
      return;
    }

    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    const incomingChatMessages = chatMessages
      .filter(msg => msg.subject === '[CHAT]' && msg.id)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const freshMessages = [];
    for (const msg of incomingChatMessages) {
      if (!knownChatNotificationIdsRef.current.has(msg.id)) {
        knownChatNotificationIdsRef.current.add(msg.id);
        if (chatNotificationsPrimedRef.current && !msg.isRead) {
          freshMessages.push(msg);
        }
      }
    }

    if (user?.id && activeIdentity?.ID) {
      try {
        sessionStorage.setItem(
          `gaia_seen_chat_notifications_${user.id}_${activeIdentity.ID}`,
          JSON.stringify(Array.from(knownChatNotificationIdsRef.current))
        );
      } catch (_) {}
    }

    if (incomingChatMessages.length > 0 && !chatNotificationsPrimedRef.current) {
      chatNotificationsPrimedRef.current = true;
      return;
    }

    if (!chatNotificationsPrimedRef.current) {
      return;
    }

    if (freshMessages.length === 0) {
      return;
    }

    const isWindowVisible = typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();
    const showNotification = msg => {
      if (!msg || msg.sender === activeIdentity.ID || parseToGaiaID(msg.sender) === ownGaia) {
        return;
      }
      if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
        return;
      }

      const isDirectMessage = !msg.channelId;
      const senderDisplayName = contacts.find(contact => parseToGaiaID(contact.gaiaID) === parseToGaiaID(msg.sender))?.displayName
        || msg.senderGaia
        || displayGaiaID(msg.sender);
      const body = String(msg.body || '');
      const preview = body.length > 90 ? `${body.slice(0, 90)}...` : body;

      if (isDirectMessage) {
        const activeDirectVisible = isWindowVisible &&
          currentMenu === 'chat' &&
          activeChatContact &&
          parseToGaiaID(activeChatContact.gaiaID) === parseToGaiaID(msg.sender);
        if (activeDirectVisible) {
          return;
        }
        new Notification(`Neue Chat-Nachricht von ${senderDisplayName}`, { body: preview || 'Neue sichere Nachricht' });
        return;
      }

      const roomName = rooms.find(room => room.ID === msg.roomId)?.Name || 'Gruppe';
      const channelName = channels.find(channel => channel.id === msg.channelId)?.name || msg.channelId || 'channel';
      const activeGroupVisible = isWindowVisible &&
        currentMenu === 'groups' &&
        activeRoom?.ID === msg.roomId &&
        activeChannel?.id === msg.channelId;
      if (activeGroupVisible) {
        return;
      }
      new Notification(`${roomName} · #${channelName}`, {
        body: `${senderDisplayName}: ${preview || 'Neue sichere Nachricht'}`
      });
    };

    freshMessages.forEach(showNotification);
  }, [activeChannel, activeChatContact, activeIdentity, activeRoom, channels, chatMessages, contacts, currentMenu, rooms, user?.id]);

  useEffect(() => {
    if (!user || !derivedKeys || isLocked || inactivityLockMinutes <= 0) return undefined;
    let timer = null;
    const lockAfterIdle = () => {
      handleLock();
      triggerAlert(t('sperren') || 'Gesperrt', t('inactivity_lock_notice') || 'GaiaCOM wurde wegen Inaktivitat kryptografisch gesperrt.', 'warning');
    };
    const resetTimer = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(lockAfterIdle, inactivityLockMinutes * 60 * 1000);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(eventName => window.addEventListener(eventName, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      if (timer) window.clearTimeout(timer);
      events.forEach(eventName => window.removeEventListener(eventName, resetTimer));
    };
  }, [derivedKeys, handleLock, inactivityLockMinutes, isLocked, t, triggerAlert, user]);

  // --- Load GaiaDrop Inbox on Menu/Identity Change ---
  useEffect(() => {
    if (activeIdentity && derivedKeys && !isLocked) {
      loadGaiaDropInbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMenu, activeIdentity, derivedKeys, isLocked]);

  // Fetch identities & cache contacts
  useEffect(() => {
    if (user) {
      fetchIdentities();
      let parsedCachedContacts = [];
      try {
        const cachedContacts = localStorage.getItem(`contacts_${user.id}`);
        parsedCachedContacts = cachedContacts ? JSON.parse(cachedContacts) : [];
      } catch (_) {
        parsedCachedContacts = [];
      }
      setContacts(parsedCachedContacts);
      api.getMailContacts()
        .then(serverContacts => {
          const mergedContacts = mergeContactRecords(parsedCachedContacts, serverContacts || []);
          setContacts(mergedContacts);
          localStorage.setItem(`contacts_${user.id}`, JSON.stringify(mergedContacts));
        })
        .catch(() => {});
      // Load user profile details
      const cachedProfile = localStorage.getItem(`profile_${user.id}`);
      if (cachedProfile) {
        const parsed = JSON.parse(cachedProfile);
        setProfileDisplayName(parsed.displayName || user.username || '');
        setProfileBio(parsed.bio || '');
        setProfileAvatar(parsed.avatar || '\u{1F916}');
      } else {
        setProfileDisplayName(user.username || '');
        setProfileBio('Sicher verschlÃƒÂ¼sselt mit GaiaCOM');
        setProfileAvatar('\u{1F916}');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user?.id || !activeIdentity?.ID) {
      knownChatNotificationIdsRef.current = new Set();
      chatNotificationsPrimedRef.current = false;
      return undefined;
    }
    try {
      const storageKey = `gaia_seen_chat_notifications_${user.id}_${activeIdentity.ID}`;
      const cached = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      knownChatNotificationIdsRef.current = new Set(Array.isArray(cached) ? cached : []);
    } catch (_) {
      knownChatNotificationIdsRef.current = new Set();
    }
    chatNotificationsPrimedRef.current = false;

    const timer = setTimeout(() => {
      chatNotificationsPrimedRef.current = true;
    }, 3000);
    return () => clearTimeout(timer);
  }, [activeIdentity?.ID, user?.id]);

  // Fetch inbox, sent folders, and rooms on timer loop
  useEffect(() => {
    let interval = null;
    if (activeIdentity && derivedKeys && !isLocked) {
      pollEmails();
      fetchRooms();
      interval = setInterval(() => {
        pollEmails();
        fetchRooms();
      }, 4000);
    } else {
      setInboxEmails([]);
      setSentEmails([]);
      setChatMessages([]);
      setRooms([]);
      setActiveRoom(null);
      setChannels([]);
      setActiveChannel(null);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdentity, derivedKeys, isLocked]);

  // Toggle warning-red SMTP class on body
  useEffect(() => {
    if (isSmtpMode && isComposing) {
      document.body.classList.add('smtp-active');
    } else {
      document.body.classList.remove('smtp-active');
    }
    return () => {
      document.body.classList.remove('smtp-active');
    };
  }, [isSmtpMode, isComposing]);

  // --- Profile Actions ---
  async function handleUnlockProfileKeys() {
    setProfileUnlockError('');
    if (!profilePasswordInput) return;
    try {
      const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
      if (!cachedEncrypted) throw new Error('Keine Mnemonic gefunden.');
      const encObj = JSON.parse(cachedEncrypted);
      const decMnemonic = await crypto.decryptMnemonic(encObj, profilePasswordInput);
      setMnemonic(decMnemonic);
      setAreKeysUnlocked(true);
      setProfilePasswordInput('');
    } catch (_) {
      setProfileUnlockError('UngÃƒÂ¼ltiges Login-Passwort.');
    }
  }

  async function handleAvatarFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const sanitizedBase64 = await sanitizeAvatarFile(file);
      setProfileAvatar(sanitizedBase64);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  function saveProfile(e) {
    if (e) e.preventDefault();
    if (!user) return;
    const profileData = {
      displayName: profileDisplayName,
      bio: profileBio,
      avatar: profileAvatar
    };
    localStorage.setItem(`profile_${user.id}`, JSON.stringify(profileData));
    setUser({ ...user, username: profileDisplayName });
    triggerAlert('Profil gespeichert', 'Dein dezentrales Benutzerprofil wurde lokal aktualisiert.');
  }

  function saveProfileData(profileData, options = {}) {
    if (!user) return;
    const nextProfile = {
      displayName: profileData.displayName || profileDisplayName || user.username || '',
      bio: profileData.bio ?? profileBio,
      avatar: profileData.avatar || profileAvatar
    };
    setProfileDisplayName(nextProfile.displayName);
    setProfileBio(nextProfile.bio);
    setProfileAvatar(nextProfile.avatar);
    localStorage.setItem(`profile_${user.id}`, JSON.stringify(nextProfile));
    setUser({ ...user, username: nextProfile.displayName });
    if (!options.silent) {
      triggerAlert('Profil gespeichert', 'Dein dezentrales Benutzerprofil wurde lokal aktualisiert.');
    }
  }

  async function handleChangePassword(e) {
    if (e) e.preventDefault();
    setPasswordChangeError('');
    if (newPasswordInput.length < 12) {
      setPasswordChangeError('Das neue Passwort muss mindestens 12 Zeichen haben.');
      return;
    }
    if (newPasswordInput !== confirmPasswordInput) {
      setPasswordChangeError('Die neuen Passwoerter stimmen nicht ueberein.');
      return;
    }
    try {
      await api.changePassword(currentPasswordInput, newPasswordInput);
      setCurrentPasswordInput('');
      setNewPasswordInput('');
      setConfirmPasswordInput('');
      triggerAlert('Passwort aktualisiert', 'Dein GaiaCOM Login-Passwort wurde geaendert.');
    } catch (err) {
      setPasswordChangeError(err.message || 'Passwort konnte nicht geaendert werden.');
    }
  }

  async function handleSetUnlockPin(pin, confirmPin) {
    if (!mnemonic) {
      throw new Error('Schluessel muessen entsperrt sein, bevor eine PIN gesetzt werden kann.');
    }
    if (!/^\d{8,12}$/.test(pin || '')) {
      throw new Error('Die PIN muss aus 8 bis 12 Ziffern bestehen.');
    }
    if (pin !== confirmPin) {
      throw new Error('Die PIN-Bestaetigung stimmt nicht ueberein.');
    }
    const encrypted = await crypto.encryptMnemonic(mnemonic, pin);
    localStorage.setItem('gaia_pin_mnemonic_enc', JSON.stringify(encrypted));
    setPinUnlockEnabled(true);
    triggerAlert('PIN aktiviert', 'GaiaCom kann auf diesem Geraet jetzt per PIN entsperrt werden.');
  }

  function handleRemoveUnlockPin() {
    localStorage.removeItem('gaia_pin_mnemonic_enc');
    setPinUnlockEnabled(false);
    triggerAlert('PIN entfernt', 'PIN-Entsperrung wurde fuer dieses Geraet deaktiviert.');
  }

  async function handleExportRecoveryBackup(recoveryPassword) {
    const backup = await buildRecoveryBackup({
      user,
      activeIdentity,
      mnemonic,
      profileDisplayName,
      profileAvatar,
      profileBio,
      password: recoveryPassword
    });
    downloadRecoveryFile(backup, profileDisplayName || user?.username || 'gaiacom');
  }

  async function handleImportRecoveryBackup(file, recoveryPassword, newLocalPassword) {
    if (!file) {
      throw new Error('Bitte Recovery-Datei auswaehlen.');
    }
    if (!newLocalPassword || newLocalPassword.length < 12) {
      throw new Error('Das neue lokale Passwort muss mindestens 12 Zeichen haben.');
    }
    const payload = await parseRecoveryBackup(await file.text(), recoveryPassword);
    const restoredKeys = crypto.deriveKeysFromMnemonic(payload.mnemonic);
    const encData = await crypto.encryptMnemonic(payload.mnemonic, newLocalPassword);
    localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(encData));
    localStorage.setItem('gaia_username', payload.user?.username || payload.profile?.displayName || 'Recovered User');

    setMnemonic(payload.mnemonic);
    setDerivedKeys(restoredKeys.keys);
    setUsernameInput(payload.user?.username || payload.profile?.displayName || '');
    setPasswordInput(newLocalPassword);

    if (user?.id && payload.profile) {
      localStorage.setItem(`profile_${user.id}`, JSON.stringify(payload.profile));
      setProfileDisplayName(payload.profile.displayName || payload.user?.username || '');
      setProfileBio(payload.profile.bio || '');
      setProfileAvatar(payload.profile.avatar || '\u{1F916}');
      setUser({ ...user, username: payload.profile.displayName || payload.user?.username || user.username });
      writeCryptoSession({ ...user, username: payload.profile.displayName || payload.user?.username || user.username }, payload.mnemonic, cryptoSessionMinutes);
    }

    triggerAlert(
      'Recovery importiert',
      user?.id
        ? 'Deine lokale Schluesseldatei wurde mit dem neuen Passwort wiederhergestellt.'
        : 'Recovery wurde entschluesselt. Mnemonic und Nutzername sind im Login vorausgefuellt.',
      'success'
    );
  }

  async function handleDeleteAccount({ currentPassword, confirmation }) {
    if (!user) {
      throw new Error(t('delete_account_not_authenticated') || 'Kein aktiver Account.');
    }
    if (!currentPassword || confirmation !== 'DELETE') {
      throw new Error(t('delete_account_confirmation_error') || 'Passwort und DELETE-Bestaetigung sind erforderlich.');
    }

    await api.deleteAccount(currentPassword);

    [
      `active_identity_${user.id}`,
      `contacts_${user.id}`,
      `profile_${user.id}`,
      `gaia_read_msgs_${user.id}`,
      `gaia_message_meta_${user.id}`,
      `gaia_vault_${user.id}`
    ].forEach(key => localStorage.removeItem(key));
    sessionStorage.removeItem('gaia_crypto_session');
    handleLogout();
    triggerAlert(t('delete_account_done_title') || 'Account geloescht', t('delete_account_done_desc') || 'Alle zuordenbaren GaiaCOM-Daten wurden geloescht.', 'warning');
  }

  async function handleDiscoverSubmit(e) {
    e.preventDefault();
    setDiscoverError('');
    setDiscoveredContact(null);
    if (!discoverGaiaId.trim()) return;

    try {
      const formatted = parseToGaiaID(discoverGaiaId);
      const res = await api.getPublicIdentity(formatted);
      if (res && res.publicRecord) {
        const pubRecord = JSON.parse(res.publicRecord);
        setDiscoveredContact({
          ID: res.id,
          gaiaID: res.gaiaID,
          displayName: res.displayName,
          publicKey: pubRecord.public_keys.identity,
          abuseScore: res.trustPassport?.abuseScore || res.abuseScore || { score: 0, escalationLevel: 0 },
          trustPassport: res.trustPassport,
          keyHistory: res.trustPassport?.keyHistory || buildInitialKeyHistory(pubRecord.public_keys.identity, true)
        });
      } else {
        setDiscoverError('Kontakt im fÃƒÂ¶derierten Netz nicht gefunden.');
      }
    } catch (err) {
      setDiscoverError(err.message);
    }
  }

  function addDiscoveredContact() {
    if (!discoveredContact || !user) return;
    const existing = contacts.find(c => c.ID === discoveredContact.ID || c.gaiaID === discoveredContact.gaiaID);
    
    const saveContact = async () => {
      const normalizedContact = {
        ...discoveredContact,
        keyHistory: existing && existing.publicKey !== discoveredContact.publicKey
          ? appendKeyHistory(existing, discoveredContact.publicKey, true)
          : (discoveredContact.keyHistory || buildInitialKeyHistory(discoveredContact.publicKey, true)),
        keyConfirmedAt: new Date().toISOString()
      };
      const persisted = await api.saveMailContact({
        id: normalizedContact.id || normalizedContact.ID,
        gaiaId: normalizedContact.gaiaID,
        displayName: normalizedContact.displayName,
        publicKey: normalizedContact.publicKey || '',
        blocked: !!normalizedContact.blocked
      });
      const mergedContact = {
        ...normalizedContact,
        ...persisted,
        gaiaID: persisted?.gaiaId || persisted?.gaiaID || normalizedContact.gaiaID
      };
      const updated = [...contacts.filter(c => c.ID !== discoveredContact.ID && c.gaiaID !== discoveredContact.gaiaID), mergedContact];
      setContacts(updated);
      localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
      setShowAddContact(false);
      triggerAlert('Kontakt hinzugefÃƒÂ¼gt', `${discoveredContact.displayName} wurde in Ihr Adressbuch eingetragen.`);
    };

    if (existing && existing.publicKey !== discoveredContact.publicKey) {
      showConfirm(
        t('key_change_warn_title') || 'Kryptographische Warnung',
        `Der ÃƒÂ¶ffentliche SchlÃƒÂ¼ssel fÃƒÂ¼r ${discoveredContact.displayName} hat sich geÃƒÂ¤ndert!\n\nAlt: ${existing.publicKey.slice(0, 20)}...\nNeu: ${discoveredContact.publicKey.slice(0, 20)}...\n\nMÃƒÂ¶chtest du den neuen SchlÃƒÂ¼ssel wirklich akzeptieren? (Dies kann ein Hinweis auf einen Man-in-the-Middle-Angriff sein!)`,
        saveContact,
        null,
        'SchlÃƒÂ¼ssel akzeptieren',
        'Abbrechen',
        true
      );
    } else {
      saveContact();
    }
  }

  async function openContactProfile(gaiaID) {
    const formatted = parseToGaiaID(gaiaID);
    const localContact = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
    try {
      let trustPassport = null;
      try {
        trustPassport = await api.getTrustPassport(formatted);
      } catch (_) {}
      if (localContact) {
        setContactProfile({
          ...localContact,
          trustPassport: trustPassport || localContact.trustPassport,
          keyHistory: localContact.keyHistory || buildInitialKeyHistory(localContact.publicKey, true)
        });
        return;
      }
      const res = await api.getPublicIdentity(formatted);
      if (res && res.publicRecord) {
        const pubRecord = JSON.parse(res.publicRecord);
        setContactProfile({
          ID: res.id,
          gaiaID: res.gaiaID,
          displayName: res.displayName,
          publicKey: pubRecord.public_keys.identity,
          abuseScore: trustPassport?.abuseScore || res.abuseScore || { score: 0, escalationLevel: 0 },
          trustPassport: trustPassport || res.trustPassport,
          keyHistory: trustPassport?.keyHistory || buildInitialKeyHistory(pubRecord.public_keys.identity, true)
        });
      }
    } catch (err) {
      triggerAlert('Profil nicht gefunden', err.message, 'danger');
    }
  }

  async function handleToggleContactBlock(gaiaID) {
    if (!activeIdentity || !gaiaID) return;
    const formatted = parseToGaiaID(gaiaID);
    const targetContact = contacts.find(contact => parseToGaiaID(contact.gaiaID) === formatted)
      || (contactProfile && parseToGaiaID(contactProfile.gaiaID) === formatted ? contactProfile : null);
    if (!targetContact) return;

    const nextBlocked = !targetContact.blocked;
    const persisted = await api.saveMailContact({
      id: targetContact.id || targetContact.ID,
      gaiaId: targetContact.gaiaID,
      displayName: targetContact.displayName,
      publicKey: targetContact.publicKey || '',
      blocked: nextBlocked
    });

    const normalized = {
      ...targetContact,
      ...persisted,
      gaiaID: persisted?.gaiaId || persisted?.gaiaID || targetContact.gaiaID,
      blocked: nextBlocked
    };

    setContacts(prev => {
      const next = prev.map(contact =>
        parseToGaiaID(contact.gaiaID) === formatted ? { ...contact, blocked: nextBlocked } : contact
      );
      localStorage.setItem(`contacts_${user.id}`, JSON.stringify(next));
      return next;
    });

    setActiveChatContact(prev => prev && parseToGaiaID(prev.gaiaID) === formatted ? { ...prev, blocked: nextBlocked } : prev);
    setContactProfile(prev => prev && parseToGaiaID(prev.gaiaID) === formatted ? { ...prev, ...normalized } : prev);
    triggerAlert(
      nextBlocked ? 'Kontakt blockiert' : 'Kontakt freigegeben',
      nextBlocked ? 'Der Kontakt wurde fuer diesen Account blockiert.' : 'Der Kontakt ist wieder freigegeben.',
      nextBlocked ? 'danger' : 'success'
    );
  }

  async function handleReportContactAbuse(gaiaID) {
    if (!activeIdentity || !gaiaID) return;
    const comment = window.prompt('Bitte gib einen Grund fuer die Meldung ein:');
    if (comment === null) return;

    await api.submitAbuseReport(
      activeIdentity.ID,
      'user',
      gaiaID,
      'harassment',
      'medium',
      null,
      comment
    );

    triggerAlert('Meldung eingereicht', 'Die Meldung wurde erfolgreich an die Governance-Moderatoren uebermittelt.', 'success');
  }

  // Selection list based on menu and mailbox states
  const activeMailsList = React.useMemo(() => {
    const isSmtpMenu = currentMenu.startsWith('smtp_');
    const folderType = isSmtpMenu ? currentMenu.substring(5) : currentMenu; 

    // Formatted drafts
    const formattedDrafts = draftsList.map(draft => {
      const isSmtpDraft = !!(draft.recipientGaia?.includes('@') && !draft.recipientGaia?.startsWith('@'));
      const msg = {
        id: draft.id,
        sender: activeIdentity?.GaiaID || '',
        senderGaia: activeIdentity?.DisplayName || activeIdentity?.displayName || activeIdentity?.GaiaID || '',
        recipient: draft.recipientGaia || '',
        recipientGaia: draft.recipientGaia ? displayGaiaID(draft.recipientGaia) : '',
        subject: draft.subject || '(Kein Betreff)',
        body: draft.body || '',
        createdAt: draft.updatedAt || draft.createdAt || new Date().toISOString(),
        isRead: true,
        isSmtp: isSmtpDraft,
        isDraft: true,
        mailbox: {
          folder: isSmtpDraft ? 'smtp_drafts' : 'drafts',
          isRead: true,
          isStarred: false,
          isImportant: false,
          isSpam: false,
          isArchived: false,
          labels: []
        }
      };
      return {
        id: draft.id,
        key: `draft_${draft.id}`,
        subject: draft.subject || '(Kein Betreff)',
        messages: [msg],
        latestMessage: msg,
        isRead: true,
        isStarred: false,
        isImportant: false,
        sender: msg.sender,
        senderGaia: msg.senderGaia,
        recipient: msg.recipient,
        recipientGaia: msg.recipientGaia,
        createdAt: msg.createdAt,
        isSmtp: isSmtpDraft,
        isDraft: true
      };
    });

    const allMailItems = [...mailThreads, ...formattedDrafts];

    return allMailItems.filter(thread => {
      if (thread.isSmtp !== isSmtpMenu) return false;

      const latest = thread.latestMessage;
      const folder = thread.isDraft 
        ? 'drafts' 
        : (latest.mailbox?.folder || (latest.sender === activeIdentity?.GaiaID || latest.sender === activeIdentity?.ID ? 'sent' : 'inbox'));
      const isSpam = latest.mailbox?.isSpam || false;

      // 1. Search filter
      if (mailboxSearch) {
        const query = mailboxSearch.toLowerCase();
        const matches = thread.messages.some(mail => 
          (mail.subject && mail.subject.toLowerCase().includes(query)) ||
          (mail.body && mail.body.toLowerCase().includes(query)) ||
          (mail.senderGaia && mail.senderGaia.toLowerCase().includes(query)) ||
          (mail.recipientGaia && mail.recipientGaia.toLowerCase().includes(query))
        );
        if (!matches) return false;
      }

      // 2. Custom label filter
      if (mailboxLabel) {
        const matchesLabel = thread.messages.some(mail => {
          const labels = mail.mailbox?.labels || [];
          return labels.includes(mailboxLabel);
        });
        if (!matchesLabel) return false;
      }

      // 3. Folder specific checks
      if (folderType === 'starred') {
        if (!thread.isStarred) return false;
        return folder !== 'trash' && !isSpam;
      }
      if (folderType === 'important') {
        if (!thread.isImportant) return false;
        return folder !== 'trash' && !isSpam;
      }
      if (folderType === 'inbox') {
        return folder === 'inbox' && !isSpam && folder !== 'trash';
      }
      if (folderType === 'drafts') {
        return thread.isDraft === true;
      }
      if (folderType === 'sent') {
        return folder === 'sent' && !thread.isDraft;
      }
      if (folderType === 'archive') {
        return folder === 'archive' || latest.mailbox?.isArchived;
      }
      if (folderType === 'spam') {
        return folder === 'spam' || isSpam;
      }
      if (folderType === 'trash') {
        return folder === 'trash';
      }
      if (folderType === 'snoozed') {
        return folder === 'snoozed' || thread.messages.some(m => !!m.mailbox?.snoozedUntil);
      }

      return true;
    });
  }, [mailThreads, draftsList, currentMenu, mailboxSearch, mailboxLabel, activeIdentity]);

  const unreadEmailsCount = React.useMemo(() => {
    return allMails.filter(mail => !mail.isSmtp && (mail.mailbox?.folder === 'inbox') && !mail.isRead && !mail.mailbox?.isSpam).length;
  }, [allMails]);

  const unreadSmtpEmailsCount = React.useMemo(() => {
    return allMails.filter(mail => mail.isSmtp && (mail.mailbox?.folder === 'inbox') && !mail.isRead && !mail.mailbox?.isSpam).length;
  }, [allMails]);

  const getUnreadChatCount = (contact) => {
    if (!activeIdentity) return 0;
    const contactGaia = parseToGaiaID(contact.gaiaID);
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    return chatMessages.filter(msg => 
      parseToGaiaID(msg.sender) === contactGaia &&
      parseToGaiaID(msg.recipient) === ownGaia &&
      !msg.isRead
    ).length;
  };

  const unreadChatsTotal = contacts.reduce((sum, c) => sum + getUnreadChatCount(c), 0);

  const getUnreadRoomCount = (room) => {
    if (!activeIdentity) return 0;
    const ownGaia = parseToGaiaID(activeIdentity.GaiaID);
    return chatMessages.filter(msg => 
      msg.roomId === room.ID && 
      parseToGaiaID(msg.sender) !== ownGaia &&
      msg.sender !== activeIdentity.ID && 
      !msg.isRead
    ).length;
  };

  const unreadRoomsTotal = rooms.reduce((sum, r) => sum + getUnreadRoomCount(r), 0);

  const formatBadgeCount = (count) => {
    if (count <= 0) return null;
    return count > 99 ? '99+' : count;
  };

  const unreadDropsCount = gaiaDropInbox.filter(drop => drop.status === 'new').length;

  // Global Keyboard Shortcuts
  useEffect(() => {
    if (mailSettings?.keyboardMode === 'disabled') return;

    let keysPressed = '';
    const handleKeyDown = (e) => {
      if (
        document.activeElement.tagName === 'INPUT' ||
        document.activeElement.tagName === 'TEXTAREA' ||
        document.activeElement.isContentEditable
      ) {
        return;
      }

      const key = e.key.toLowerCase();
      keysPressed += key;
      if (keysPressed.length > 2) {
        keysPressed = keysPressed.slice(-2);
      }

      if (keysPressed === 'gi') {
        setCurrentMenu('inbox');
        setSelectedMail(null);
        setIsComposing(false);
        keysPressed = '';
        return;
      }
      if (keysPressed === 'gs') {
        setCurrentMenu('sent');
        setSelectedMail(null);
        keysPressed = '';
        return;
      }
      if (keysPressed === 'gc') {
        setCurrentMenu('chat');
        keysPressed = '';
        return;
      }
      if (keysPressed === 'gg') {
        setCurrentMenu('groups');
        keysPressed = '';
        return;
      }
      if (keysPressed === 'gp') {
        setCurrentMenu('profile');
        setActiveProfileSection('edit');
        keysPressed = '';
        return;
      }

      if (key === 'c') {
        setIsComposing(true);
        setComposeTo('');
        setComposeSubject('');
        setComposeBody('');
        setComposeReplyTo(null);
        setSelectedMail(null);
        if (activeDraftIdRef) {
          activeDraftIdRef.current = null;
        }
        e.preventDefault();
      } else if (key === 'r' && selectedMail) {
        handleReplyMail(selectedMail);
        e.preventDefault();
      } else if (key === 'f' && selectedMail) {
        handleReplyMail(selectedMail, { forward: true });
        e.preventDefault();
      } else if (key === 'e' && selectedMail) {
        updateMailboxState(selectedMail, { folder: 'archive', isArchived: true });
        triggerAlert('Archiviert', 'Die Mail wurde archiviert.');
        setSelectedMail(null);
        e.preventDefault();
      } else if ((key === 'delete' || key === 'backspace' || key === '#') && selectedMail) {
        updateMailboxState(selectedMail, { folder: 'trash' });
        triggerAlert('Gelöscht', 'Die Mail wurde in den Papierkorb verschoben.');
        setSelectedMail(null);
        e.preventDefault();
      } else if (key === 's' && selectedMail) {
        const nextStarred = !selectedMail.mailbox?.isStarred;
        updateMailboxState(selectedMail, { isStarred: nextStarred });
        triggerAlert(nextStarred ? 'Markiert' : 'Entmarkt', nextStarred ? 'Stern hinzugefügt.' : 'Stern entfernt.');
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mailSettings, selectedMail, handleReplyMail, updateMailboxState, triggerAlert, setComposeBody, setComposeReplyTo, setComposeSubject, setComposeTo, setIsComposing, setSelectedMail, setCurrentMenu, setActiveProfileSection, activeDraftIdRef]);

  useEffect(() => {
    if (!user?.id || !derivedKeys || isLocked || showWizard) {
      if (!user?.id || !derivedKeys) {
        bootSequenceSeenRef.current = '';
      }
      setShowBootSequence(false);
      return;
    }
    if (bootSequenceSeenRef.current === user.id) {
      return;
    }
    bootSequenceSeenRef.current = user.id;
    setShowBootSequence(false);
    const frame = window.requestAnimationFrame(() => setShowBootSequence(true));
    const timer = window.setTimeout(() => setShowBootSequence(false), 2600);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [derivedKeys, isLocked, showWizard, user?.id]);

  useEffect(() => {
    if (!user?.id || !activeIdentity?.ID || !derivedKeys || isLocked || showWizard) {
      setShowFirstRunOnboarding(false);
      onboardingSeenKeyRef.current = '';
      return undefined;
    }

    const storageKey = `gaia_first_run_onboarding_${user.id}_${activeIdentity.ID}`;
    if (onboardingSeenKeyRef.current === storageKey) {
      return undefined;
    }
    onboardingSeenKeyRef.current = storageKey;

    if (localStorage.getItem(storageKey) === 'done') {
      setShowFirstRunOnboarding(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowFirstRunOnboarding(true);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [activeIdentity?.ID, derivedKeys, isLocked, showWizard, user?.id]);

  const closeFirstRunOnboarding = useCallback(() => {
    if (user?.id && activeIdentity?.ID) {
      localStorage.setItem(`gaia_first_run_onboarding_${user.id}_${activeIdentity.ID}`, 'done');
    }
    setShowFirstRunOnboarding(false);
  }, [activeIdentity?.ID, user?.id]);

  const openOnboardingDestination = useCallback((menu, afterOpen = null) => {
    closeFirstRunOnboarding();
    setCurrentMenu(menu);
    setSelectedMail(null);
    setIsComposing(false);
    setMobileMenuOpen(false);
    if (afterOpen) afterOpen();
  }, [closeFirstRunOnboarding, setIsComposing, setSelectedMail]);

  const hasActiveContent = !!(
    selectedMail ||
    isComposing ||
    currentMenu === 'dashboard' ||
    (currentMenu === 'chat' && activeChatContact) ||
    (currentMenu === 'groups' && activeRoom) ||
    (currentMenu === 'public_channels' && (activePublicChannel || publicChannelCreatorOpen)) ||
    currentMenu === 'network_health' ||
    currentMenu === 'abuse_center' ||
    currentMenu === 'security_center' ||
    (currentMenu === 'profile' && activeProfileSection) ||
    currentMenu === 'vault' ||
    (currentMenu === 'gaiadrop' && selectedDrop) ||
    currentMenu === 'gsn'
  );

  // --- RENDER SCREEN ---

  // Unlock Screen
  if (isLocked) {
    return (
      <UnlockScreen
        unlockPassword={unlockPassword}
        unlockError={unlockError}
        pinEnabled={pinUnlockEnabled}
        onPasswordChange={setUnlockPassword}
        onSubmit={handleUnlock}
        onLogout={handleLogout}
      />
    );
  }

  // 1. Auth Page
  if (!user || !derivedKeys) {
    return (
      <AuthScreen
        isRegister={isRegister}
        usernameInput={usernameInput}
        passwordInput={passwordInput}
        mnemonic={mnemonic}
        copiedMnemonic={copiedMnemonic}
        authError={authError}
        showRegSuccessPopup={showRegSuccessPopup}
        derivedKeys={derivedKeys}
        serverVersion={serverVersion}
        serverConsensus={serverConsensus}
        dropTargetInput={dropTargetInput}
        dropSenderInput={dropSenderInput}
        dropMessageInput={dropMessageInput}
        dropStatus={dropStatus}
        dropError={dropError}
        onSubmit={handleAuthSubmit}
        onImportRecovery={handleImportRecoveryBackup}
        onUsernameChange={setUsernameInput}
        onPasswordChange={setPasswordInput}
        onMnemonicChange={setMnemonic}
        onToggleMode={() => {
          setIsRegister(!isRegister);
          setAuthError('');
        }}
        onGenerateMnemonic={handleGenerateMnemonic}
        onCopyMnemonic={() => {
          navigator.clipboard.writeText(mnemonic);
          setCopiedMnemonic(true);
        }}
        onCloseSuccess={() => {
          setShowRegSuccessPopup(false);
          setUser({ id: tempUserId, username: usernameInput, allowAnonymousStats: true });
        }}
        onSubmitGaiaDrop={handleSubmitPublicGaiaDrop}
        onDropTargetChange={setDropTargetInput}
        onDropSenderChange={setDropSenderInput}
        onDropMessageChange={setDropMessageInput}
        t={t}
      />
    );
  }

  // 2. Setup Wizard (Profile generator)
  if (showWizard) {
    return (
      <SetupWizard
        wizardStep={wizardStep}
        mnemonic={mnemonic}
        copiedMnemonic={copiedMnemonic}
        wizardGaiaUsername={wizardGaiaUsername}
        wizardDomain={wizardDomain}
        wizardCustomDomain={wizardCustomDomain}
        wizardFallbackNodes={wizardFallbackNodes}
        availableNodes={availableNodes}
        wizardError={wizardError}
        derivedKeys={derivedKeys}
        t={t}
        onCopiedMnemonicChange={setCopiedMnemonic}
        onStepChange={setWizardStep}
        onGaiaUsernameChange={setWizardGaiaUsername}
        onDomainChange={setWizardDomain}
        onCustomDomainChange={setWizardCustomDomain}
        onFallbackNodesChange={setWizardFallbackNodes}
        onRegisterIdentity={handleWizardRegisterIdentity}
      />
    );
  }

  const contentWide = mailListCollapsed || currentMenu === 'dashboard' || currentMenu === 'network_health' || currentMenu === 'abuse_center' || currentMenu === 'security_center' || currentMenu === 'gsn';

  return (
    <>
      {showBootSequence && (
        <div className="gaia-boot-overlay" aria-hidden="true">
          <div className="gaia-boot-grid"></div>
          <div className="gaia-boot-wordmark">GAIACOM</div>
          <div className="gaia-boot-subline">NETWORK // QUANTUM SECURE // READY</div>
        </div>
      )}

      {showTransition && (
        <div className="gaia-transition-overlay" aria-hidden="true">
          <div className="gaia-transition-logo-container">
            <div className="gaia-transition-ring"></div>
            <div className="gaia-transition-text">{transitionText}</div>
            <div className="gaia-transition-subtext">Quantum Shield Enabled</div>
          </div>
        </div>
      )}

      {showFirstRunOnboarding && !showTransition && (
        <FirstRunOnboarding
          activeIdentity={activeIdentity}
          displayGaiaID={displayGaiaID}
          profileDisplayName={profileDisplayName}
          profileAvatar={profileAvatar}
          onProfileSave={(profileData) => saveProfileData(profileData, { silent: true })}
          onComplete={closeFirstRunOnboarding}
          onSkip={closeFirstRunOnboarding}
          onOpenInbox={() => openOnboardingDestination('inbox')}
          onOpenChat={() => openOnboardingDestination('chat')}
          onOpenChannels={() => openOnboardingDestination('public_channels', () => setPublicChannelCreatorOpen(false))}
          onOpenSecurity={() => openOnboardingDestination('security_center')}
        />
      )}

      <div className={`app-container ${isLightMode ? 'light-mode' : 'dark-mode'} ${hasActiveContent ? 'has-active-content mobile-content-active' : ''} ${contentWide ? 'mail-list-collapsed' : ''} ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        {/* COLUMN 1: NAVIGATION SIDEBAR */}
      <NavigationSidebar
        setActiveProfileSection={setActiveProfileSection}
        activeIdentity={activeIdentity}
        unreadDropsCount={unreadDropsCount}
        displayGaiaID={displayGaiaID}
        currentMenu={currentMenu}
        setCurrentMenu={setCurrentMenu}
        setIsComposing={setIsComposing}
        setSelectedMail={setSelectedMail}
        unreadEmailsCount={unreadEmailsCount}
        unreadSmtpEmailsCount={unreadSmtpEmailsCount}
        unreadChatsTotal={unreadChatsTotal}
        unreadRoomsTotal={unreadRoomsTotal}
        contacts={contacts}
        activeChatContact={activeChatContact}
        setActiveChatContact={setActiveChatContact}
        rooms={rooms}
        activeRoom={activeRoom}
        setActiveRoom={setActiveRoom}
        setPublicChannelCreatorOpen={setPublicChannelCreatorOpen}
        identities={identities}
        setShowWizard={setShowWizard}
        isLightMode={isLightMode}
        setIsLightMode={setIsLightMode}
        language={language}
        changeLanguage={changeLanguage}
        handleLock={handleLock}
        handleLogout={handleLogout}
        setShowQuantumShieldModal={setShowQuantumShieldModal}
        serverVersion={serverVersion}
        serverConsensus={serverConsensus}
        setMobileMenuOpen={setMobileMenuOpen}
        t={t}
        formatBadgeCount={formatBadgeCount}
      />

      {/* COLUMN 2: LIST PANE */}
      {!contentWide && (
        <ListPane
          currentMenu={currentMenu}
          setCurrentMenu={setCurrentMenu}
          contacts={contacts}
          setContacts={setContacts}
          rooms={rooms}
          activeRoom={activeRoom}
          setActiveRoom={setActiveRoom}
          publicChannels={publicChannels}
          activePublicChannel={activePublicChannel}
          setActivePublicChannel={setActivePublicChannel}
          setPublicChannelCreatorOpen={setPublicChannelCreatorOpen}
          publicChannelsLoading={publicChannelsLoading}
          refreshPublicChannels={refreshPublicChannels}
          activeChatContact={activeChatContact}
          setActiveChatContact={setActiveChatContact}
          selectedMail={selectedMail}
          setSelectedMail={setSelectedMail}
          setIsComposing={setIsComposing}
          setComposeTo={setComposeTo}
          setComposeSubject={setComposeSubject}
          setComposeBody={setComposeBody}
          setComposeReplyTo={setComposeReplyTo}
          setMobileMenuOpen={setMobileMenuOpen}
          activeMailsList={activeMailsList}
          getUnreadChatCount={getUnreadChatCount}
          getUnreadRoomCount={getUnreadRoomCount}
          formatBadgeCount={formatBadgeCount}
          setMailListCollapsed={setMailListCollapsed}
          setContactProfile={setContactProfile}
          openContactProfile={openContactProfile}
          vaultUnlocked={vaultUnlocked}
          vaultRecords={vaultRecords}
          selectedVaultRecord={selectedVaultRecord}
          setSelectedVaultRecord={setSelectedVaultRecord}
          gaiaDropInbox={gaiaDropInbox}
          selectedDrop={selectedDrop}
          setSelectedDrop={handleSelectDrop}
          activeIdentity={activeIdentity}
          loadGaiaDropInbox={loadGaiaDropInbox}
          gaiaDropLoading={gaiaDropLoading}
          user={user}
          t={t}
          displayGaiaID={displayGaiaID}
          parseToGaiaID={parseToGaiaID}
          buildInitialKeyHistory={buildInitialKeyHistory}
          triggerAlert={triggerAlert}
          showConfirm={showConfirm}
          setShowCreateGroupModal={setShowCreateGroupModal}
          setShowJoinGroupModal={setShowJoinGroupModal}
          activeProfileSection={activeProfileSection}
          setActiveProfileSection={setActiveProfileSection}
          handleClearDirectChat={handleClearDirectChat}
          handleLeaveRoom={handleLeaveRoom}
          handleDeleteGroup={handleDeleteGroup}
          chatMessages={chatMessages}
          setChatMessages={setChatMessages}
          fetchRooms={fetchRooms}
          mailboxFolder={mailboxFolder}
          setMailboxFolder={setMailboxFolder}
          mailboxSearch={mailboxSearch}
          setMailboxSearch={setMailboxSearch}
          mailboxLabel={mailboxLabel}
          setMailboxLabel={setMailboxLabel}
          labelsList={labelsList}
          draftsList={draftsList}
          updateMailboxState={updateMailboxState}
          snoozeMail={snoozeMail}
          saveLabel={saveLabel}
          activeDraftIdRef={activeDraftIdRef}
          setIsSmtpMode={setIsSmtpMode}
        />
      )}

      <AppMainContent
        chatUploadProgress={chatUploadProgress}
        uploadChatFile={uploadChatFile}
        toggleBlockContact={toggleBlockContact}
        slowModeCooldowns={slowModeCooldowns}
        pinnedMessageIds={pinnedMessageIds}
        onToggleMessagePin={handleToggleMessagePin}
        handleKickMember={handleKickMember}
        handleTransferOwnership={handleTransferOwnership}
        handleGetJoinRequests={handleGetJoinRequests}
        handleModerateJoinRequest={handleModerateJoinRequest}
        handleGetModerationLogs={handleGetModerationLogs}
        handleSearchPublicRooms={handleSearchPublicRooms}
        handleCreateRoomInviteLink={handleCreateRoomInviteLink}
        handleJoinViaInviteLink={handleJoinViaInviteLink}
        handleCreateJoinRequest={handleCreateJoinRequest}
        joinRequests={joinRequests}
        moderationLogs={moderationLogs}
        publicRoomsSearchResult={publicRoomsSearchResult}
        hasActiveContent={hasActiveContent}
        currentMenu={currentMenu}
        setMobileMenuOpen={setMobileMenuOpen}
        t={t}
        mailListCollapsed={mailListCollapsed}
        setMailListCollapsed={setMailListCollapsed}
        rooms={rooms}
        contacts={contacts}
        chatMessages={chatMessages}
        inboxEmails={inboxEmails}
        activeIdentity={activeIdentity}
        setCurrentMenu={setCurrentMenu}
        setActiveChatContact={setActiveChatContact}
        setActiveRoom={setActiveRoom}
        setShowCreateGroupModal={setShowCreateGroupModal}
        displayGaiaID={displayGaiaID}
        deferredPrompt={deferredPrompt}
        handleInstallApp={handleInstallApp}
        activeChatContact={activeChatContact}
        presenceMap={presenceMap}
        chatInputText={chatInputText}
        setChatInputText={setChatInputText}
        handleSendChatMessage={handleSendChatMessage}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        handleDeleteChatMessage={handleDeleteChatMessage}
        handleEditChatMessage={handleEditChatMessage}
        setInboxEmails={setInboxEmails}
        setSentEmails={setSentEmails}
        handleClearDirectChat={handleClearDirectChat}
        openContactProfile={openContactProfile}
        messageMeta={messageMeta}
        toggleMessagePin={toggleMessagePin}
        toggleMessageSaved={toggleMessageSaved}
        reactToMessage={reactToMessage}
        activeDirectUnreadMarker={activeDirectUnreadMarker}
        messageReplyTarget={messageReplyTarget}
        setMessageReplyTarget={setMessageReplyTarget}
        getSenderRoles={getSenderRoles}
        activeRoom={activeRoom}
        channels={channels}
        activeChannel={activeChannel}
        setActiveChannel={setActiveChannel}
        handleSendGroupMessage={handleSendGroupMessage}
        handleUpdateMemberRole={handleUpdateMemberRole}
        handleLeaveRoom={handleLeaveRoom}
        setShowCreateChannelModal={setShowCreateChannelModal}
        triggerAlert={triggerAlert}
        handleOpenGroupSettings={handleOpenGroupSettings}
        handleClearGroupChannel={handleClearGroupChannel}
        activeGroupUnreadMarker={activeGroupUnreadMarker}
        activePublicChannel={activePublicChannel}
        setActivePublicChannel={setActivePublicChannel}
        publicChannelCreatorOpen={publicChannelCreatorOpen}
        setPublicChannelCreatorOpen={setPublicChannelCreatorOpen}
        publicChannelPosts={publicChannelPosts}
        publicChannelsError={publicChannelsError}
        publicChannelPostsLoading={publicChannelPostsLoading}
        createPublicChannel={createPublicChannel}
        updatePublicChannel={updatePublicChannel}
        togglePublicChannelSubscription={togglePublicChannelSubscription}
        createPublicChannelPost={createPublicChannelPost}
        togglePublicChannelPostReaction={togglePublicChannelPostReaction}
        createPublicChannelPostComment={createPublicChannelPostComment}
        togglePublicChannelPostPin={togglePublicChannelPostPin}
        updatePublicChannelComments={updatePublicChannelComments}
        reportChannel={reportChannel}
        deleteChannel={deleteChannel}
        verifyChannel={verifyChannel}
        discoverResults={discoverResults}
        discoverLoading={discoverLoading}
        handleBlockChannel={handleBlockChannel}
        handleUnblockChannel={handleUnblockChannel}
        handleDiscoverChannels={handleDiscoverChannels}
        handleDeleteComment={handleDeleteComment}
        handleModerateComment={handleModerateComment}
        showConfirm={showConfirm}
        isComposing={isComposing}
        isSmtpMode={isSmtpMode}
        setIsSmtpMode={setIsSmtpMode}
        composeTo={composeTo}
        setComposeTo={setComposeTo}
        composeSubject={composeSubject}
        setComposeSubject={setComposeSubject}
        composeBody={composeBody}
        setComposeBody={setComposeBody}
        composeScheduledFor={composeScheduledFor}
        setComposeScheduledFor={setComposeScheduledFor}
        fileInputRef={fileInputRef}
        handleFileUpload={handleFileUpload}
        uploadFile={uploadFile}
        uploadProgress={uploadProgress}
        composeError={composeError}
        handleSendMail={handleSendMail}
        setIsComposing={setIsComposing}
        selectedMail={selectedMail}
        selectedMailProof={selectedMailProof}
        handleReplyMail={handleReplyMail}
        handleExportDisclosurePackage={handleExportDisclosurePackage}
        handleReportMail={handleReportMail}
        setSelectedMail={setSelectedMail}
        profileAvatar={profileAvatar}
        setProfileAvatar={setProfileAvatar}
        profileDisplayName={profileDisplayName}
        setProfileDisplayName={setProfileDisplayName}
        profileBio={profileBio}
        setProfileBio={setProfileBio}
        saveProfile={saveProfile}
        handleAvatarFileChange={handleAvatarFileChange}
        currentPasswordInput={currentPasswordInput}
        setCurrentPasswordInput={setCurrentPasswordInput}
        newPasswordInput={newPasswordInput}
        setNewPasswordInput={setNewPasswordInput}
        confirmPasswordInput={confirmPasswordInput}
        setConfirmPasswordInput={setConfirmPasswordInput}
        passwordChangeError={passwordChangeError}
        handleChangePassword={handleChangePassword}
        areKeysUnlocked={areKeysUnlocked}
        profilePasswordInput={profilePasswordInput}
        setProfilePasswordInput={setProfilePasswordInput}
        handleUnlockProfileKeys={handleUnlockProfileKeys}
        profileUnlockError={profileUnlockError}
        derivedKeys={derivedKeys}
        mnemonic={mnemonic}
        setAreKeysUnlocked={setAreKeysUnlocked}
        activeProfileSection={activeProfileSection}
        setActiveProfileSection={setActiveProfileSection}
        cryptoSessionMinutes={cryptoSessionMinutes}
        setCryptoSessionMinutes={setCryptoSessionMinutes}
        inactivityLockMinutes={inactivityLockMinutes}
        setInactivityLockMinutes={setInactivityLockMinutes}
        pinUnlockEnabled={pinUnlockEnabled}
        handleSetUnlockPin={handleSetUnlockPin}
        handleRemoveUnlockPin={handleRemoveUnlockPin}
        handleDeleteAccount={handleDeleteAccount}
        handleExportRecoveryBackup={handleExportRecoveryBackup}
        user={user}
        handleUpdatePrivacySettings={handleUpdatePrivacySettings}
        vaultUnlocked={vaultUnlocked}
        vaultPasswordInput={vaultPasswordInput}
        setVaultPasswordInput={setVaultPasswordInput}
        vaultError={vaultError}
        vaultRecords={vaultRecords}
        vaultDraftTitle={vaultDraftTitle}
        setVaultDraftTitle={setVaultDraftTitle}
        vaultDraftCategory={vaultDraftCategory}
        setVaultDraftCategory={setVaultDraftCategory}
        vaultDraftBody={vaultDraftBody}
        setVaultDraftBody={setVaultDraftBody}
        handleDeleteVaultRecord={handleDeleteVaultRecord}
        handleUnlockVault={handleUnlockVault}
        handleLockVault={handleLockVault}
        selectedVaultRecord={selectedVaultRecord}
        setSelectedVaultRecord={setSelectedVaultRecord}
        gaiaDropInbox={gaiaDropInbox}
        selectedDrop={selectedDrop}
        setSelectedDrop={setSelectedDrop}
        loadGaiaDropInbox={loadGaiaDropInbox}
        gaiaDropLoading={gaiaDropLoading}
        handleDeleteDrop={handleDeleteDrop}
        getUnreadChatCount={getUnreadChatCount}
        getUnreadRoomCount={getUnreadRoomCount}
        formatBadgeCount={formatBadgeCount}
        setContactProfile={setContactProfile}
        chatInputRef={fileInputRef}
        fetchRooms={fetchRooms}
        mailboxFolder={mailboxFolder}
        setMailboxFolder={setMailboxFolder}
        mailboxSearch={mailboxSearch}
        setMailboxSearch={setMailboxSearch}
        mailboxLabel={mailboxLabel}
        setMailboxLabel={setMailboxLabel}
        labelsList={labelsList}
        draftsList={draftsList}
        filterRules={filterRules}
        mailSettings={mailSettings}
        isSavingDraft={isSavingDraft}
        updateMailboxState={updateMailboxState}
        snoozeMail={snoozeMail}
        saveLabel={saveLabel}
        saveFilterRule={saveFilterRule}
        saveSettings={saveSettings}
        allMails={allMails}
      />

      <AppFeedbackModals
        alertConfig={alertConfig}
        setAlertConfig={setAlertConfig}
        confirmConfig={confirmConfig}
        setConfirmConfig={setConfirmConfig}
        t={t}
      />
      <AppModalLayer
        contactProfile={contactProfile}
        setContactProfile={setContactProfile}
        showAddContact={showAddContact}
        setShowAddContact={setShowAddContact}
        discoverGaiaId={discoverGaiaId}
        setDiscoverGaiaId={setDiscoverGaiaId}
        handleDiscoverSubmit={handleDiscoverSubmit}
        discoverError={discoverError}
        discoveredContact={discoveredContact}
        addDiscoveredContact={addDiscoveredContact}
        displayGaiaID={displayGaiaID}
        t={t}
        showQuantumShieldModal={showQuantumShieldModal}
        setShowQuantumShieldModal={setShowQuantumShieldModal}
        showCreateGroupModal={showCreateGroupModal}
        setShowCreateGroupModal={setShowCreateGroupModal}
        groupNameInput={groupNameInput}
        setGroupNameInput={setGroupNameInput}
        groupDescriptionInput={groupDescriptionInput}
        setGroupDescriptionInput={setGroupDescriptionInput}
        groupAvatarInput={groupAvatarInput}
        setGroupAvatarInput={setGroupAvatarInput}
        handleCreateRoom={handleCreateRoom}
        isCrisisRoomInput={isCrisisRoomInput}
        setIsCrisisRoomInput={setIsCrisisRoomInput}
        showJoinGroupModal={showJoinGroupModal}
        setShowJoinGroupModal={setShowJoinGroupModal}
        joinGroupHashInput={joinGroupHashInput}
        setJoinGroupHashInput={setJoinGroupHashInput}
        handleJoinRoom={handleJoinRoom}
        showCreateChannelModal={showCreateChannelModal}
        setShowCreateChannelModal={setShowCreateChannelModal}
        newChannelNameInput={newChannelNameInput}
        setNewChannelNameInput={setNewChannelNameInput}
        handleCreateChannel={handleCreateChannel}
        showGroupSettingsModal={showGroupSettingsModal}
        setShowGroupSettingsModal={setShowGroupSettingsModal}
        editGroupName={editGroupName}
        setEditGroupName={setEditGroupName}
        editGroupDescription={editGroupDescription}
        setEditGroupDescription={setEditGroupDescription}
        editGroupAvatar={editGroupAvatar}
        setEditGroupAvatar={setEditGroupAvatar}
        editGroupIsCrisis={editGroupIsCrisis}
        setEditGroupIsCrisis={setEditGroupIsCrisis}
        editGroupIsPrivate={editGroupIsPrivate}
        setEditGroupIsPrivate={setEditGroupIsPrivate}
        editGroupReadOnly={editGroupReadOnly}
        setEditGroupReadOnly={setEditGroupReadOnly}
        editGroupSlowModeSeconds={editGroupSlowModeSeconds}
        setEditGroupSlowModeSeconds={setEditGroupSlowModeSeconds}
        handleUpdateGroupSettings={handleUpdateGroupSettings}
        handleDeleteGroup={handleDeleteGroup}
        handleUpdateMemberRole={handleUpdateMemberRole}
        handleKickMember={handleKickMember}
        handleTransferOwnership={handleTransferOwnership}
        handleGetJoinRequests={handleGetJoinRequests}
        handleModerateJoinRequest={handleModerateJoinRequest}
        joinRequests={joinRequests}
        handleGetModerationLogs={handleGetModerationLogs}
        moderationLogs={moderationLogs}
        handleCreateRoomInviteLink={handleCreateRoomInviteLink}
        activeRoom={activeRoom}
        activeIdentity={activeIdentity}
        keyChangeWarning={keyChangeWarning}
        keyChangeConfirmInput={keyChangeConfirmInput}
        setKeyChangeConfirmInput={setKeyChangeConfirmInput}
        activeChatContact={activeChatContact}
        currentMenu={currentMenu}
        onToggleContactBlock={handleToggleContactBlock}
        onReportContact={handleReportContactAbuse}
        onClearActiveChat={handleClearDirectChat}
        onCloseActiveChat={() => {
          setContactProfile(null);
          setActiveChatContact(null);
        }}
      />
      </div>
    </>
  );
}


